import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, Bomb as BombIcon, CircleOff, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getLobeIcon } from '@/lib/lobe-icon'
import { formatQuotaWithCurrency } from '@/lib/currency'
import { useSlotMachineHistory } from '../hooks/use-slot-machine'
import { SYMBOL_MAP } from '../types'

function formatTime(unix: number): string {
  const d = new Date(unix * 1000)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const mins = String(d.getMinutes()).padStart(2, '0')
  return `${month}-${day} ${hours}:${mins}`
}

export function SlotMachineHistory() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const { data, isLoading } = useSlotMachineHistory(page, 10)

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-6'>
        <span className='text-sm text-muted-foreground'>{t('Loading...')}</span>
      </div>
    )
  }

  if (!data || data.records.length === 0) {
    return (
      <div className='flex items-center justify-center py-6'>
        <span className='text-sm text-muted-foreground'>{t('No records yet')}</span>
      </div>
    )
  }

  const totalPages = Math.ceil(data.total / data.page_size)

  return (
    <div className='space-y-2'>
      {data.records.map((record) => {
        const symbols = record.symbols.split(',')
        return (
          <div key={record.id} className='flex items-center justify-between py-1.5 border-b border-border/40 last:border-0'>
            <div className='flex items-center gap-2'>
              <span className='text-xs text-muted-foreground w-14 shrink-0'>{formatTime(record.created_at)}</span>
              <div className='flex gap-0.5'>
                {symbols.map((sym, i) => {
                  const symbol = SYMBOL_MAP[sym]
                  return (
                    <span key={i} className='flex items-center justify-center' title={symbol?.label}>
                      {sym === 'bomb' ? (
                        <BombIcon className='h-4 w-4' style={{ color: symbol?.color }} />
                      ) : symbol?.lobeIcon ? (
                        getLobeIcon(symbol.lobeIcon, 16)
                      ) : (
                        <span className='text-xs font-bold' style={{ color: symbol?.color }}>
                          {symbol?.label?.charAt(0) || '?'}
                        </span>
                      )}
                    </span>
                  )
                })}
              </div>
            </div>
            <div className='flex items-center gap-1 shrink-0'>
              {record.result === 'win' && <Sparkles className='h-3 w-3 text-emerald-500' />}
              {record.result === 'bomb' && <BombIcon className='h-3 w-3 text-red-500' />}
              {record.result === 'loss' && <CircleOff className='h-3 w-3 text-muted-foreground' />}
              <span
                className={`text-xs font-medium ${
                  record.reward_quota > 0
                    ? 'text-emerald-500'
                    : record.reward_quota < 0
                    ? 'text-red-500'
                    : 'text-muted-foreground'
                }`}
              >
                {record.reward_quota > 0 ? '+' : ''}{record.reward_quota > 0 ? formatQuotaWithCurrency(record.reward_quota) : record.reward_quota < 0 ? formatQuotaWithCurrency(Math.abs(record.reward_quota)) : formatQuotaWithCurrency(0)}
              </span>
            </div>
          </div>
        )
      })}
      {totalPages > 1 && (
        <div className='flex items-center justify-between pt-2'>
          <Button variant='ghost' size='sm' disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <span className='text-xs text-muted-foreground'>
            {page} / {totalPages}
          </span>
          <Button variant='ghost' size='sm' disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      )}
    </div>
  )
}
